<template>
  <div v-if="show" class="modal-overlay" @click.self="onClose">
    <div class="modal-container">
      <div class="modal-header">
        <h2>{{ filename }}</h2>
        <div class="header-actions">
          <button class="dl-btn" @click="$emit('download')">Download</button>
          <button class="close-btn" @click="onClose">✕</button>
        </div>
      </div>
      <div class="modal-body">
        <div v-if="state === 'loading'" class="loading-state">Loading preview…</div>
        <div v-else-if="state === 'error'" class="error-message">{{ errorMessage }}</div>
        <template v-else>
          <div v-if="sheetNames.length > 1" class="sheet-tabs">
            <button
              v-for="name in sheetNames"
              :key="name"
              :class="{ active: activeSheet === name }"
              class="sheet-tab"
              @click="activeSheet = name"
            >{{ name }}</button>
          </div>
          <div class="table-wrap">
            <table class="data-table preview-table">
              <thead>
                <tr>
                  <th v-for="(h, i) in headerRow" :key="i">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, r) in activeRows" :key="r">
                  <td v-for="(cell, c) in row" :key="c">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
            <p v-if="activeRows.length === 0" class="no-data">Sheet is empty.</p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';

export default {
  name: 'ReconFilePreviewModal',
  props: {
    show: { type: Boolean, default: false },
    filename: { type: String, default: '' },
    loadData: { type: Function, required: true }
  },
  emits: ['close', 'download'],
  data: function() {
    return {
      state: 'loading',
      errorMessage: '',
      sheets: {},
      sheetNames: [],
      activeSheet: ''
    };
  },
  computed: {
    headerRow: function() {
      var rows = this.sheets[this.activeSheet];
      return rows && rows.length > 0 ? rows[0] : [];
    },
    activeRows: function() {
      var rows = this.sheets[this.activeSheet];
      return rows && rows.length > 1 ? rows.slice(1) : [];
    }
  },
  watch: {
    show: function(val) {
      if (val) this.loadPreview();
    }
  },
  methods: {
    onClose: function() {
      this.$emit('close');
    },
    fmtCell: function(cell) {
      if (cell === null || cell === undefined || cell === '') return '';
      if (typeof cell === 'number') {
        return Number.isInteger(cell) ? String(cell) : cell.toFixed(2).replace(/0+$/, '').replace(/\.$/, '.0');
      }
      if (cell instanceof Date) return cell.toISOString().slice(0, 10);
      return String(cell);
    },
    loadPreview: function() {
      var self = this;
      this.state = 'loading';
      this.errorMessage = '';
      this.sheets = {};
      this.sheetNames = [];
      this.activeSheet = '';
      this.loadData().then(function(result) {
        var isCsv = /\.csv$/i.test(self.filename);
        var wb = isCsv
          ? XLSX.read(result.text, { type: 'string' })
          : XLSX.read(result.arrayBuffer, { type: 'array', cellDates: true, dense: true });
        var out = {};
        wb.SheetNames.forEach(function(name) {
          var ws = wb.Sheets[name];
          var rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });
          out[name] = rows.map(function(row) { return row.map(self.fmtCell); });
        });
        self.sheets = out;
        self.sheetNames = wb.SheetNames;
        self.activeSheet = wb.SheetNames[0] || '';
        self.state = 'loaded';
      }).catch(function(err) {
        self.state = 'error';
        self.errorMessage = (err && err.message) ? err.message : 'Failed to parse file';
      });
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.modal-container {
  background: #fff; border-radius: 12px; width: 1240px; max-width: 96vw;
  max-height: 92vh; display: flex; flex-direction: column;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
}
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 20px; border-bottom: 1px solid #e2e8f0;
}
.modal-header h2 {
  font-size: 0.8125rem; font-weight: 700; color: #0f172a; margin: 0;
  font-family: ui-monospace, monospace; word-break: break-all;
}
.header-actions { display: flex; gap: 8px; align-items: center; flex-shrink: 0; margin-left: 16px; }
.close-btn { background: none; border: none; font-size: 1rem; cursor: pointer; color: #64748b; }
.dl-btn { background: #f1f5f9; color: #4f46e5; border: 1px solid #e2e8f0; padding: 5px 14px; border-radius: 8px; font-size: 0.8125rem; font-weight: 600; cursor: pointer; }
.modal-body { padding: 16px 20px 20px; overflow: hidden; display: flex; flex-direction: column; }
.sheet-tabs { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 10px; }
.sheet-tab {
  background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; padding: 5px 12px;
  border-radius: 6px; font-size: 0.75rem; font-weight: 600; cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.sheet-tab.active { background: #4f46e5; color: #fff; border-color: #4f46e5; }
.table-wrap { overflow: auto; max-height: calc(92vh - 150px); border: 1px solid #e2e8f0; border-radius: 8px; }
.data-table { border-collapse: collapse; width: 100%; font-size: 0.75rem; }
.preview-table th {
  position: sticky; top: 0; z-index: 1; text-align: left; padding: 8px 10px;
  background: #002060; color: #fff; font-size: 0.6875rem; font-weight: 600; white-space: nowrap;
}
.preview-table td { padding: 5px 10px; border-bottom: 1px solid #f1f5f9; color: #334155; white-space: nowrap; }
.loading-state { color: #64748b; font-size: 0.875rem; padding: 24px; }
.error-message { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; padding: 12px 16px; border-radius: 8px; font-size: 0.875rem; }
.no-data { font-size: 0.875rem; color: #94a3b8; text-align: center; padding: 32px 16px; }
</style>
